/**
 * Polygon rasterization at sub-cell resolution and classification of cells into the 22 shape classes.
 */
import type { Vec2 } from '../model/format.ts';
import { EMPTY, FULL, SHAPES, SHAPE_COUNT, insideShape } from './shapes.ts';

/** sample offsets keep samples off every allowed edge line (no ties on 45° / 2:1 / 1:2 lines) */
const EPS_X = 0.0011;
const EPS_Y = 0.0003;

export interface SampleGrid {
  /** cell bounds: cells x0..x0+w-1, y0..y0+h-1 */
  x0: number;
  y0: number;
  w: number;
  h: number;
  /** samples per cell edge */
  S: number;
  /** sample owner (index into the paint list), -1 = empty; row-major from the bottom, (w*S) x (h*S) */
  owner: Int16Array;
}

export function ringsBounds(rings: Vec2[][]): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of rings)
    for (const [x, y] of r) {
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x > x1) x1 = x;
      if (y > y1) y1 = y;
    }
  return [x0, y0, x1, y1];
}

export function createGrid(x0: number, y0: number, w: number, h: number, S: number): SampleGrid {
  const owner = new Int16Array(w * S * h * S);
  owner.fill(-1);
  return { x0, y0, w, h, S, owner };
}

/** Paint the even-odd interior of `rings` with `value` (-1 erases). */
export function paintRings(g: SampleGrid, rings: Vec2[][], value: number) {
  const { S, x0, y0 } = g;
  const W = g.w * S;
  const H = g.h * S;
  const xs: number[] = [];
  const [bx0, by0, bx1, by1] = ringsBounds(rings);
  const j0 = Math.max(0, Math.floor((by0 - y0) * S) - 1);
  const j1 = Math.min(H - 1, Math.ceil((by1 - y0) * S) + 1);
  if (bx1 < x0 || bx0 > x0 + g.w) return;
  for (let j = j0; j <= j1; j++) {
    const y = y0 + (j + 0.5) / S + EPS_Y;
    xs.length = 0;
    for (const ring of rings) {
      const n = ring.length;
      for (let i = 0, k = n - 1; i < n; k = i++) {
        const [xa, ya] = ring[k];
        const [xb, yb] = ring[i];
        if (ya > y !== yb > y) xs.push(xa + ((y - ya) * (xb - xa)) / (yb - ya));
      }
    }
    if (xs.length < 2) continue;
    xs.sort((a, b) => a - b);
    const row = j * W;
    for (let p = 0; p + 1 < xs.length; p += 2) {
      let i0 = Math.ceil((xs[p] - x0 - EPS_X) * S - 0.5);
      let i1 = Math.ceil((xs[p + 1] - x0 - EPS_X) * S - 0.5);
      if (i0 < 0) i0 = 0;
      if (i1 > W) i1 = W;
      for (let i = i0; i < i1; i++) g.owner[row + i] = value;
    }
  }
}

/** Exact sampled masks of each shape class (same sample positions as the rasterizer). */
const maskCache = new Map<number, { exact: Map<number, number>; masks: Uint8Array[] }>();
export function classMasks(S: number) {
  let m = maskCache.get(S);
  if (m) return m;
  const exact = new Map<number, number>();
  const masks: Uint8Array[] = [];
  for (let id = 0; id < SHAPE_COUNT; id++) {
    const mk = new Uint8Array(S * S);
    let bits = 0;
    for (let b = 0; b < S; b++)
      for (let a = 0; a < S; a++) {
        const inside = insideShape(id, (a + 0.5) / S + EPS_X, (b + 0.5) / S + EPS_Y);
        mk[b * S + a] = inside ? 1 : 0;
        if (inside && S <= 5) bits += 2 ** (b * S + a);
      }
    masks.push(mk);
    if (!exact.has(bits)) exact.set(bits, id);
  }
  m = { exact, masks };
  maskCache.set(S, m);
  return m;
}

export interface CellClass {
  /** shape per cell (row-major from the bottom, w x h) */
  shape: Int8Array;
  /** cell indices whose samples do not form a single supported shape */
  bad: number[];
}

/**
 * Classify the cells owned by `isMine(owner)` into shape classes. Cells whose sample pattern is not an
 * exact class (or whose class is not allowed) get the nearest allowed class and are reported.
 */
export function classifyCells(g: SampleGrid, isMine: (owner: number) => boolean, allowed: (id: number) => boolean): CellClass {
  const { S, w, h } = g;
  const W = w * S;
  const { exact, masks } = classMasks(S);
  const shape = new Int8Array(w * h);
  const bad: number[] = [];
  const sm = new Uint8Array(S * S);
  for (let cy = 0; cy < h; cy++)
    for (let cx = 0; cx < w; cx++) {
      let bits = 0;
      let n = 0;
      for (let b = 0; b < S; b++) {
        const row = (cy * S + b) * W + cx * S;
        for (let a = 0; a < S; a++) {
          const v = isMine(g.owner[row + a]) ? 1 : 0;
          sm[b * S + a] = v;
          if (v) {
            n++;
            bits += 2 ** (b * S + a);
          }
        }
      }
      const ci = cy * w + cx;
      if (n === 0) {
        shape[ci] = EMPTY;
        continue;
      }
      if (n === S * S) {
        shape[ci] = FULL;
        continue;
      }
      const id = exact.get(bits);
      if (id !== undefined && allowed(id)) {
        shape[ci] = id;
        continue;
      }
      // nearest allowed class
      let best = n * 2 >= S * S ? FULL : EMPTY;
      let bestE = Infinity;
      for (let k = 0; k < SHAPE_COUNT; k++) {
        if (!allowed(k)) continue;
        const mk = masks[k];
        let e = 0;
        for (let i = 0; i < mk.length; i++) e += mk[i] !== sm[i] ? 1 : 0;
        if (e < bestE) {
          bestE = e;
          best = k;
        }
      }
      shape[ci] = best;
      bad.push(ci);
    }
  return { shape, bad };
}

export function shapeKindAllowed(id: number, dirs: { s45: boolean; s21: boolean; s12: boolean }): boolean {
  const k = SHAPES[id].kind;
  return k === 'empty' || k === 'full' || dirs[k];
}

/**
 * L1 (4-connected) distance in samples from every sample to the nearest sample not owned
 * (out-of-grid counts as not owned). Returns Float32Array in sample units.
 */
export function sampleDistance(g: SampleGrid, isMine: (owner: number) => boolean): Float32Array {
  const W = g.w * g.S;
  const H = g.h * g.S;
  const d = new Float32Array(W * H);
  const INF = 1e9;
  for (let j = 0; j < H; j++)
    for (let i = 0; i < W; i++) {
      const k = j * W + i;
      if (!isMine(g.owner[k])) {
        d[k] = 0;
        continue;
      }
      let v = INF;
      v = Math.min(v, i > 0 ? d[k - 1] + 1 : 1, j > 0 ? d[k - W] + 1 : 1);
      d[k] = v;
    }
  for (let j = H - 1; j >= 0; j--)
    for (let i = W - 1; i >= 0; i--) {
      const k = j * W + i;
      if (d[k] === 0) continue;
      d[k] = Math.min(d[k], i < W - 1 ? d[k + 1] + 1 : 1, j < H - 1 ? d[k + W] + 1 : 1);
    }
  return d;
}
